import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar, CircularProgress, Icon, IconButton, Select, Spacer, Stack, Typography } from '@emburse/embark-core';
import { FileDownloadIcon } from '@emburse/embark-icons';
import VerticallyCenteredLayout from '../../components/Layouts/VerticallyCenteredLayout';
import { $t, useTranslation } from '../../hooks/useTranslation';
import { downloadUsageReport, getUsageReports } from '../../api/usageReports';
import { logger } from '../../util/logger';
const usageReportDescriptions = {
    'active-user': {
        Title: 'Active User Report',
        Description: `This report shows each expense report which was submitted during a given time span 
        and the users who submitted the reports. This will also include expense reports which were submitted and then 
        subsequently deleted. For the purposes of this report, a user is considered active if they submit an expense report 
        during the specified time span. Use this report to track usage if your organization is billed per Active User.`
    },
    'entitled-user': {
        Title: 'Entitled User Report',
        Description: `This report provides a list of user records which were active during a particular billing period. 
        For the purposes of this report, a user is considered active if they were eligible to log in to the application at any 
        time during a particular billing period. Use this report to track usage if your organization is billed on an entitled 
        user usage model.`
    },
    'expense-reports-processed': {
        Title: 'Expense Reports Processed',
        Description: `This report shows each expense report which was processed during a given time span. This 
        will include expense reports which were processed and subsequently unprocessed or deleted. For the purposes of usage, 
        expense reports processed multiple times are only counted once. Use this report to track usage if your organization 
        is billed on a transactional usage model.`
    }
};
const urlString = window.location.href;
const url = new URL(urlString);
const reportType = url.searchParams.get('report_type');
function UsageReports() {
    useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [selectedReport, setSelectedReport] = useState('');
    const [availableReports, setAvailableReports] = useState([]);
    const [isLoadingReports, setIsLoadingReports] = useState(true);
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    useEffect(() => {
        if (!reportType || !Object.keys(usageReportDescriptions).includes(reportType)) {
            window.location.href = '/Home.aspx';
        }
        (async () => {
            try {
                const reports = await getUsageReports(reportType);
                reports.forEach((report, index) => {
                    reports[index] = report.replace('.csv', '');
                });
                setSelectedReport(reports[0]);
                setAvailableReports(reports);
                if (reports.length === 0) {
                    enqueueSnackbar($t('No reports are available.'), { variant: 'info' });
                }
            }
            catch (error) {
                setAvailableReports([]);
                enqueueSnackbar($t('Failed to load reports. Please refresh the page to try to load reports again. Contact Support if the problem persists.'), { variant: 'error' });
                logger.error(error);
            }
            finally {
                setIsLoadingReports(false);
            }
        })();
    }, [enqueueSnackbar]);
    const onDownloadClick = useCallback(async (event) => {
        event.stopPropagation();
        setIsDownloadingReport(true);
        try {
            const report = await downloadUsageReport(reportType, selectedReport);
            const blob = new Blob([report], { type: 'text/plain' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `${selectedReport}.csv`;
            a.click();
        }
        catch (error) {
            enqueueSnackbar($t('Failed to download report. Please try again. Contact Support if the problem persists.'), { variant: 'error' });
            logger.error(error);
        }
        finally {
            setIsDownloadingReport(false);
        }
    }, [enqueueSnackbar, selectedReport]);
    return (_jsxs(VerticallyCenteredLayout, { children: [_jsx(Stack, { spacing: 2, sx: { maxWidth: '900px' }, alignItems: "flex-start", children: reportType && reportType.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h4", children: $t(usageReportDescriptions[reportType].Title) }), _jsx(Typography, { children: $t(usageReportDescriptions[reportType].Description) })] })) }), _jsx(Spacer, { size: 50 }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { width: '100%', maxWidth: '900px' }, children: [_jsx(Select, { label: $t('Billing Period'), id: "report-selection", value: selectedReport, onChange: (e) => {
                            setSelectedReport(e.target.value);
                        }, sx: { maxWidth: '500px' }, disabled: isLoadingReports || isDownloadingReport || availableReports.length === 0, fullWidth: true, native: true, children: availableReports.map((report) => (_jsx("option", { value: report, children: report }, report))) }), _jsx(IconButton, { size: "small", "aria-label": $t('download'), disabled: isLoadingReports || isDownloadingReport || availableReports.length === 0, onClick: onDownloadClick, children: isLoadingReports || isDownloadingReport ? (_jsx(CircularProgress, { size: 20 })) : (_jsx(Icon, { iconUrl: FileDownloadIcon })) })] })] }));
}
export default UsageReports;
